import { useState, useCallback } from 'react';

export interface UseFormSubmissionOptions {
  onSubmit: () => Promise<void> | void;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export interface UseFormSubmissionReturn {
  isSubmitting: boolean;
  error: string;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
  setError: (error: string) => void;
  reset: () => void;
}

/**
 * Hook réutilisable pour gérer la soumission de formulaires
 * Gère l'état de chargement, les erreurs et les callbacks
 */
export function useFormSubmission({
  onSubmit,
  onSuccess,
  onError,
}: UseFormSubmissionOptions): UseFormSubmissionReturn {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setError('');
      setIsSubmitting(true);

      try {
        await onSubmit();
        onSuccess?.();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Une erreur est survenue';
        setError(errorMessage);
        onError?.(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
    [onSubmit, onSuccess, onError]
  );

  const reset = useCallback(() => {
    setIsSubmitting(false);
    setError('');
  }, []);

  return {
    isSubmitting,
    error,
    handleSubmit,
    setError,
    reset,
  };
}